import type { ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import api from '../api/auth.api';
import { sitesApi } from '../api/sites.api';
import { invoicesApi } from '../api/invoices.api';
import { approvalsApi } from '../api/approvals.api';
import { syncApi, type SyncStatus } from '../api/sync.api';
import { SkeletonCard } from '../components/SkeletonCard';

interface DashboardSummary {
  subscribers: { total: number; eligible: number };
  invoices: { total: number; dueToday: number; overdue: number; paid: number };
  financial: { totalOutstanding: number; currency: string };
  reminders: { sentToday: number };
}

interface DueAgingBucket {
  count: number;
  amount: number;
}

interface DueAging {
  current: DueAgingBucket;
  days1to30: DueAgingBucket;
  days31to60: DueAgingBucket;
  days61to90: DueAgingBucket;
  over90: DueAgingBucket;
}

interface ReminderDailyTrend {
  date: string;
  sent: number | string;
  failed: number | string;
  skipped: number | string;
}

interface ReminderStats {
  statusBreakdown: Record<string, number>;
  dailyTrend: ReminderDailyTrend[];
  period: { from: string; to: string };
}

interface SitePerformanceRow {
  id: number;
  name: string;
  subscriberCount: number;
  outstandingAmount: number;
}

function todayIst(): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatCount(value: number): string {
  return value.toLocaleString('en-IN');
}

function formatDateTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return new Date(iso).toLocaleString('en-IN', {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function sumOverdueAmount(aging: DueAging): number {
  return (
    aging.days1to30.amount +
    aging.days31to60.amount +
    aging.days61to90.amount +
    aging.over90.amount
  );
}

async function safeFetch<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch {
    return fallback;
  }
}

function findTodayTrend(trend: ReminderDailyTrend[], today: string): ReminderDailyTrend | undefined {
  return trend.find((entry) => String(entry.date).startsWith(today));
}

function findLastReminderDate(trend: ReminderDailyTrend[]): string | null {
  for (let i = trend.length - 1; i >= 0; i -= 1) {
    if (Number(trend[i].sent) > 0) {
      return String(trend[i].date);
    }
  }
  return null;
}

function formatSyncResultSummary(result: Record<string, unknown> | null): string | null {
  if (!result) return null;

  const parts: string[] = [];
  const sites = result.sites as { discovered?: number; created?: number; updated?: number } | undefined;
  const subscribers = result.subscribers as {
    created?: number;
    updated?: number;
    skipped?: number;
    failed?: number;
  } | undefined;
  const invoices = result.invoices as { totalInvoices?: number } | undefined;

  if (sites) {
    const siteCount = sites.discovered ?? (sites.created ?? 0) + (sites.updated ?? 0);
    if (siteCount > 0) parts.push(`${siteCount} site${siteCount === 1 ? '' : 's'}`);
  }
  if (subscribers) {
    const subCount = (subscribers.created ?? 0) + (subscribers.updated ?? 0);
    if (subCount > 0) parts.push(`${subCount} subscriber${subCount === 1 ? '' : 's'}`);
    if ((subscribers.failed ?? 0) > 0) {
      parts.push(`${subscribers.failed} subscriber error${subscribers.failed === 1 ? '' : 's'}`);
    }
  }
  if (invoices && (invoices.totalInvoices ?? 0) > 0) {
    parts.push(`${invoices.totalInvoices} invoice${invoices.totalInvoices === 1 ? '' : 's'}`);
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

function syncStatusLabel(status: SyncStatus['status']): string {
  switch (status) {
    case 'running':
      return 'Running';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return 'Idle';
  }
}

function syncStatusBadgeClass(status: SyncStatus['status']): string {
  switch (status) {
    case 'running':
      return 'bg-blue-100 text-blue-700';
    case 'completed':
      return 'bg-green-100 text-green-700';
    case 'failed':
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}

interface AutomationRunSummary {
  id: number;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  runSlotKey: string | null;
  totalCandidates: number;
  totalSent: number;
  totalSkipped: number;
  totalFailed: number;
  error: string | null;
}

interface AutomationRunWithLinks extends AutomationRunSummary {
  linksCreated: number;
}

interface PaymentLinkGenerationStatus {
  isRunning: boolean;
  isReminderCronRunning: boolean;
  isBulkSyncRunning: boolean;
  scheduledCronEnabled: boolean;
  note: string;
  lastBulkRun: {
    finishedAt: string;
    syncType: string;
    created: number;
    regenerated: number;
    skipped: number;
    errors: number;
  } | null;
  viaReminderCron: {
    lastRun: AutomationRunWithLinks | null;
    activeRun: AutomationRunWithLinks | null;
  };
}

interface AutomationJobsStatus {
  reminderAutomation: {
    enabled: boolean;
    isRunning: boolean;
    runTimes: string[];
    timezone: string;
    lastScheduledRun: AutomationRunSummary | null;
    activeRun: AutomationRunSummary | null;
  };
  upcomingDueReminder: {
    enabled: boolean;
    isRunning: boolean;
    runTime: string;
    timezone: string;
    daysBeforeDue: number;
    frequencyDays: number;
    lastRun: AutomationRunWithLinks | null;
    activeRun: AutomationRunWithLinks | null;
  };
  paymentLinkGeneration: PaymentLinkGenerationStatus;
}

function automationRunStatusLabel(
  isRunning: boolean,
  run: AutomationRunSummary | null
): string {
  if (isRunning) return 'Running';
  if (!run) return 'Never run';
  if (run.status === 'completed') return 'Completed';
  if (run.status === 'interrupted') return 'Interrupted';
  if (run.status === 'failed') return 'Failed';
  return run.status;
}

function automationRunBadgeClass(
  isRunning: boolean,
  run: AutomationRunSummary | null
): string {
  if (isRunning) return 'bg-blue-100 text-blue-700';
  if (!run) return 'bg-gray-100 text-gray-600';
  if (run.status === 'completed') return 'bg-green-100 text-green-700';
  if (run.status === 'interrupted') return 'bg-amber-100 text-amber-800';
  if (run.status === 'failed') return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-600';
}

function resolveAutomationDisplayRun(
  isRunning: boolean,
  activeRun: AutomationRunSummary | null | undefined,
  lastRun: AutomationRunSummary | null | undefined
): AutomationRunSummary | null {
  if (isRunning && activeRun) return activeRun;
  return lastRun ?? null;
}

function formatAutomationRunStats(run: AutomationRunSummary): string {
  return `${run.totalSent} sent · ${run.totalSkipped} skipped · ${run.totalFailed} failed`;
}

function formatAutomationRunStatsWithLinks(run: AutomationRunWithLinks): string {
  return `${run.linksCreated ?? 0} links created · ${run.totalSent} sent · ${run.totalSkipped} skipped · ${run.totalFailed} failed`;
}

function paymentLinkStatusLabel(jobs: PaymentLinkGenerationStatus): string {
  if (jobs.isRunning) return 'Running';

  const run = jobs.viaReminderCron?.activeRun ?? jobs.viaReminderCron?.lastRun;
  if (!run) {
    return jobs.lastBulkRun ? 'Bulk only' : 'Never run';
  }

  if (run.status === 'failed') return 'Failed';
  if (run.status === 'running') return 'Running';

  if (run.linksCreated === 0 && run.totalFailed > 0) return 'Failed';
  if (run.linksCreated > 0 && run.totalFailed > 0) return 'Partial';
  if (run.linksCreated > 0) return 'Completed';

  return 'No links created';
}

function paymentLinkBadgeClass(jobs: PaymentLinkGenerationStatus): string {
  const label = paymentLinkStatusLabel(jobs);

  if (label === 'Running') return 'bg-blue-100 text-blue-700';
  if (label === 'Completed') return 'bg-green-100 text-green-700';
  if (label === 'Partial') return 'bg-amber-100 text-amber-800';
  if (label === 'Failed' || label === 'No links created') return 'bg-red-100 text-red-700';
  return 'bg-gray-100 text-gray-600';
}

function formatPaymentLinkRunStats(run: AutomationRunWithLinks): string {
  return `${run.linksCreated} links created · ${run.totalFailed} failed · ${run.totalSkipped} skipped`;
}

function KpiCard({
  label,
  value,
  href,
  loading,
}: {
  label: string;
  value: string;
  href: string;
  loading?: boolean;
}) {
  return (
    <a
      href={href}
      className="block bg-white rounded-lg shadow-sm border border-gray-100 p-6 hover:border-gray-200 hover:shadow transition-all"
    >
      {loading ? (
        <div className="animate-pulse space-y-3">
          <div className="h-3 bg-gray-200 rounded w-2/3" />
          <div className="h-8 bg-gray-200 rounded w-1/2" />
        </div>
      ) : (
        <>
          <p className="text-3xl font-bold text-gray-900 tracking-tight">{value}</p>
          <p className="mt-2 text-sm text-gray-500">{label}</p>
        </>
      )}
    </a>
  );
}

function SectionCard({
  title,
  children,
  className = '',
}: {
  title: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-100 p-6 ${className}`}
    >
      <h2 className="text-base font-semibold text-gray-900 mb-5">{title}</h2>
      {children}
    </div>
  );
}

function MetricRow({
  label,
  value,
  valueClassName = 'text-gray-900',
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-600">{label}</span>
      <span className={`text-base font-semibold ${valueClassName}`}>{value}</span>
    </div>
  );
}

function ActionRow({
  label,
  count,
  href,
  countClassName = 'text-gray-900',
}: {
  label: string;
  count: number;
  href: string;
  countClassName?: string;
}) {
  return (
    <a
      href={href}
      className="flex items-center justify-between px-4 py-3.5 rounded-lg border border-gray-100 hover:bg-gray-50 hover:border-gray-200 transition-colors"
    >
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <span className={`text-lg font-semibold ${countClassName}`}>
        {formatCount(count)}
      </span>
    </a>
  );
}

function EmptyState({
  message,
  actionLabel,
  actionHref,
}: {
  message: string;
  actionLabel?: string;
  actionHref?: string;
}) {
  return (
    <div className="py-10 text-center">
      <p className="text-sm text-gray-500">{message}</p>
      {actionLabel && actionHref && (
        <a
          href={actionHref}
          className="inline-block mt-3 text-sm font-medium text-blue-600 hover:text-blue-700"
        >
          {actionLabel}
        </a>
      )}
    </div>
  );
}

async function fetchDashboardData() {
  const today = todayIst();

  const emptySummary: DashboardSummary = {
    subscribers: { total: 0, eligible: 0 },
    invoices: { total: 0, dueToday: 0, overdue: 0, paid: 0 },
    financial: { totalOutstanding: 0, currency: 'INR' },
    reminders: { sentToday: 0 },
  };

  const emptyAging: DueAging = {
    current: { count: 0, amount: 0 },
    days1to30: { count: 0, amount: 0 },
    days31to60: { count: 0, amount: 0 },
    days61to90: { count: 0, amount: 0 },
    over90: { count: 0, amount: 0 },
  };

  const [
    sitesResult,
    invoiceStats,
    summary,
    dueAging,
    reminderStatsToday,
    reminderStatsHistory,
    approvalsResult,
    upcomingInvoices,
  ] = await Promise.all([
    safeFetch(() => sitesApi.list(), { sites: [], total: 0 }),
    safeFetch(() => invoicesApi.getStats(), {
      total: 0,
      due_today: 0,
      overdue: 0,
      paid: 0,
      unpaid: 0,
      total_outstanding: 0,
    }),
    safeFetch(
      () =>
        api
          .get<{ success: boolean; data: DashboardSummary }>('/dashboard/summary')
          .then((res) => res.data.data),
      emptySummary
    ),
    safeFetch(
      () =>
        api
          .get<{ success: boolean; data: DueAging }>('/dashboard/due-aging')
          .then((res) => res.data.data),
      emptyAging
    ),
    safeFetch(
      () =>
        api
          .get<{ success: boolean; data: ReminderStats }>('/dashboard/reminder-stats', {
            params: { from: today, to: today },
          })
          .then((res) => res.data.data),
      { statusBreakdown: {}, dailyTrend: [], period: { from: today, to: today } }
    ),
    safeFetch(
      () =>
        api
          .get<{ success: boolean; data: ReminderStats }>('/dashboard/reminder-stats')
          .then((res) => res.data.data),
      { statusBreakdown: {}, dailyTrend: [], period: { from: today, to: today } }
    ),
    safeFetch(
      () => approvalsApi.listPending({ page: 1, limit: 5 }),
      {
        approvals: [],
        pagination: { page: 1, limit: 5, total: 0, totalPages: 0 },
      }
    ),
    safeFetch(
      () => invoicesApi.listInvoices({ due_status: 'upcoming', per_page: 1 }),
      { invoices: [], pagination: { current_page: 1, total_pages: 0, per_page: 1, total_items: 0 } }
    ),
  ]);

  const sites = sitesResult.sites;
  const siteOutstandingResults = await Promise.allSettled(
    sites.map(async (site) => {
      const outstanding = await safeFetch(
        () =>
          api
            .get<{ success: boolean; data: DashboardSummary }>('/dashboard/summary', {
              params: { site_id: site.id },
            })
            .then((res) => res.data.data.financial.totalOutstanding),
        0
      );

      return {
        id: site.id,
        name: site.name,
        subscriberCount: site.subscriberCount,
        outstandingAmount: outstanding,
      } satisfies SitePerformanceRow;
    })
  );

  const siteOutstanding = siteOutstandingResults
    .filter((result): result is PromiseFulfilledResult<SitePerformanceRow> => result.status === 'fulfilled')
    .map((result) => result.value);

  const topSites = [...siteOutstanding]
    .sort(
      (a, b) =>
        b.outstandingAmount - a.outstandingAmount || b.subscriberCount - a.subscriberCount
    )
    .slice(0, 5);

  const dueThisWeek =
    invoiceStats.due_today + (upcomingInvoices.pagination?.total_items ?? 0);

  const todayTrend = findTodayTrend(reminderStatsToday.dailyTrend ?? [], today);
  const lastReminderDate = findLastReminderDate(reminderStatsHistory.dailyTrend ?? []);
  const lastReminderTime = lastReminderDate ? `${lastReminderDate}T12:00:00+05:30` : null;

  return {
    sites,
    totalSites: sitesResult.total,
    totalSubscribers: summary.subscribers.total,
    totalOutstanding: invoiceStats.total_outstanding,
    pendingApprovals: approvalsResult.pagination.total,
    recentApprovals: approvalsResult.approvals,
    paymentCollection: {
      outstandingAmount: invoiceStats.total_outstanding,
      dueTodayAmount: dueAging.current.amount,
      overdueAmount: sumOverdueAmount(dueAging),
      pendingInvoices: invoiceStats.unpaid,
    },
    reminderActivity: {
      sentToday: summary.reminders.sentToday,
      successfulSends: Number(todayTrend?.sent ?? reminderStatsToday.statusBreakdown.sent ?? 0),
      failedSends: Number(todayTrend?.failed ?? reminderStatsToday.statusBreakdown.failed ?? 0),
      lastReminderTime,
    },
    actionItems: {
      overdueSubscribers: summary.invoices.overdue,
      dueThisWeek,
      pendingApprovalPayments: approvalsResult.pagination.total,
    },
    topSites,
  };
}

export function DashboardPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['dashboard'],
    queryFn: fetchDashboardData,
    refetchInterval: 60_000,
    retry: 1,
  });

  const { data: apiHealth, isLoading: isHealthLoading } = useQuery({
    queryKey: ['crm-api-health'],
    queryFn: async () => {
      const response = await api.get<{
        success: boolean;
        data: {
          ok?: boolean;
          status?: string;
          docsUrl?: string;
          docsReachable?: boolean;
          authValid?: boolean;
          responseTimeMs?: number;
          error?: string;
        };
      }>('/dashboard/crm-api-health');
      return response.data.data;
    },
    refetchInterval: 30_000,
    retry: 1,
  });

  const { data: syncStatus, isLoading: isSyncStatusLoading } = useQuery({
    queryKey: ['sync-status'],
    queryFn: () => syncApi.getStatus(),
    refetchInterval: (query) =>
      query.state.data?.status === 'running' ? 3_000 : 30_000,
    retry: 1,
  });

  const {
    data: automationJobs,
    isLoading: isAutomationJobsLoading,
    isError: isAutomationJobsError,
  } = useQuery({
    queryKey: ['automation-jobs-status'],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: AutomationJobsStatus }>(
        '/dashboard/automation-jobs-status'
      );
      return response.data.data;
    },
    refetchInterval: (query) => {
      const data = query.state.data;
      const isActive =
        data?.reminderAutomation?.isRunning ||
        data?.upcomingDueReminder?.isRunning ||
        data?.paymentLinkGeneration?.isRunning ||
        data?.paymentLinkGeneration?.isReminderCronRunning;
      return isActive ? 3_000 : 15_000;
    },
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: 1,
  });

  const syncMutation = useMutation({
    mutationFn: () => syncApi.queueSync(),
    onSuccess: (result) => {
      if (result.queued === false) {
        toast(
          result.reason === 'sync_already_running' || result.reason === 'job_already_queued'
            ? 'Sync is already running'
            : 'Sync could not be queued'
        );
        return;
      }
      toast.success('CRM sync started in the background.');
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['sync-status'] });
      queryClient.invalidateQueries({ queryKey: ['automation-jobs-status'] });
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'Failed to start sync';
      toast.error(message);
    },
  });

  const hasSites = (data?.totalSites ?? 0) > 0;
  const showLoadError = !isLoading && isError;
  const showGlobalEmpty = !isLoading && !showLoadError && !hasSites;

  const paymentLinkJobs = automationJobs?.paymentLinkGeneration;
  const paymentLinkCronRun =
    paymentLinkJobs?.viaReminderCron?.activeRun ??
    paymentLinkJobs?.viaReminderCron?.lastRun ??
    null;

  const reminderCronRunning = automationJobs?.reminderAutomation?.isRunning ?? false;
  const reminderCronDisplayRun = resolveAutomationDisplayRun(
    reminderCronRunning,
    automationJobs?.reminderAutomation?.activeRun,
    automationJobs?.reminderAutomation?.lastScheduledRun
  );

  const upcomingCronRunning = automationJobs?.upcomingDueReminder?.isRunning ?? false;
  const upcomingCronDisplayRun = resolveAutomationDisplayRun(
    upcomingCronRunning,
    automationJobs?.upcomingDueReminder?.activeRun,
    automationJobs?.upcomingDueReminder?.lastRun
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <div className="flex flex-wrap items-start gap-3">
          <div
            className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm min-w-[180px]"
            title={
              apiHealth?.error ||
              (apiHealth?.docsUrl
                ? `CRM: ${apiHealth.docsUrl}${apiHealth.responseTimeMs ? ` (${apiHealth.responseTimeMs}ms)` : ''}`
                : 'CRM API health')
            }
          >
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              CRM API Health
            </p>
            {isHealthLoading ? (
              <p className="text-xs text-gray-500 mt-1">Checking...</p>
            ) : (
              <>
                <span
                  className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                    apiHealth?.status === 'healthy' || apiHealth?.ok
                      ? 'bg-green-100 text-green-700'
                      : 'bg-red-100 text-red-700'
                  }`}
                >
                  {apiHealth?.status === 'healthy' || apiHealth?.ok ? 'Healthy' : 'Unhealthy'}
                </span>
                {apiHealth?.docsReachable === false || apiHealth?.authValid === false ? (
                  <p className="text-[10px] text-red-600 mt-1">
                    {!apiHealth?.docsReachable ? 'Docs unreachable' : 'Auth failed'}
                  </p>
                ) : null}
              </>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm min-w-[220px] max-w-[280px]">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              Last CRM Sync
            </p>
            {isSyncStatusLoading ? (
              <p className="text-xs text-gray-500 mt-1">Loading...</p>
            ) : (
              <div className="mt-1 space-y-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${syncStatusBadgeClass(syncStatus?.status ?? 'idle')}`}
                  >
                    {syncStatusLabel(syncStatus?.status ?? 'idle')}
                  </span>
                  {syncStatus?.status === 'running' && syncStatus.progress.percent > 0 ? (
                    <span className="text-[10px] text-gray-500">
                      {syncStatus.progress.percent}%
                    </span>
                  ) : null}
                </div>
                {syncStatus?.status === 'running' && syncStatus.phase ? (
                  <p className="text-[10px] text-gray-600 leading-snug">{syncStatus.phase}</p>
                ) : null}
                <p className="text-[10px] text-gray-600">
                  Last completed:{' '}
                  {formatDateTime(syncStatus?.lastSyncedAt) ?? 'No sync completed yet'}
                </p>
                {syncStatus?.nextSyncAt ? (
                  <p className="text-[10px] text-gray-500">
                    Next scheduled:{' '}
                    {syncStatus.minutesUntilNextSync > 0
                      ? `in ${syncStatus.minutesUntilNextSync} min`
                      : formatDateTime(syncStatus.nextSyncAt) ?? 'soon'}
                  </p>
                ) : null}
                {formatSyncResultSummary(syncStatus?.result ?? null) ? (
                  <p className="text-[10px] text-gray-600 leading-snug">
                    {formatSyncResultSummary(syncStatus?.result ?? null)}
                  </p>
                ) : null}
                {syncStatus?.status === 'failed' && syncStatus.error ? (
                  <p className="text-[10px] text-red-600 leading-snug">{syncStatus.error}</p>
                ) : null}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-gray-200 bg-white px-3 py-2 shadow-sm min-w-[240px] max-w-[300px]">
            <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
              Automation Jobs
            </p>
            {isAutomationJobsLoading ? (
              <p className="text-xs text-gray-500 mt-1">Loading...</p>
            ) : isAutomationJobsError || !automationJobs ? (
              <p className="text-xs text-amber-700 mt-1">
                Unable to load automation status. Refresh the page or restart the backend.
              </p>
            ) : (
              <div className="mt-2 space-y-2">
                <div className="space-y-1">
                  <p className="text-[10px] font-semibold text-gray-700 uppercase tracking-wide">
                    Reminders (CRON)
                  </p>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${automationRunBadgeClass(
                        reminderCronRunning,
                        reminderCronDisplayRun
                      )}`}
                    >
                      {automationRunStatusLabel(reminderCronRunning, reminderCronDisplayRun)}
                    </span>
                    {!automationJobs?.reminderAutomation?.enabled ? (
                      <span className="text-[10px] text-amber-700">Disabled</span>
                    ) : null}
                  </div>
                  <p className="text-[10px] text-gray-600">
                    {reminderCronRunning ? 'Current run' : 'Last run'}:{' '}
                    {formatDateTime(
                      reminderCronDisplayRun?.startedAt ??
                        reminderCronDisplayRun?.finishedAt
                    ) ?? 'No scheduled run yet'}
                  </p>
                  {automationJobs?.reminderAutomation?.enabled &&
                  (automationJobs.reminderAutomation?.runTimes.length ?? 0) > 0 ? (
                    <p className="text-[10px] text-gray-500">
                      Schedule: {automationJobs.reminderAutomation.runTimes.join(', ')} (
                      {automationJobs.reminderAutomation.timezone})
                    </p>
                  ) : null}
                  {reminderCronDisplayRun ? (
                    <p className="text-[10px] text-gray-600">
                      {formatAutomationRunStats(reminderCronDisplayRun)}
                      {reminderCronRunning && reminderCronDisplayRun.totalCandidates > 0
                        ? ` · ${reminderCronDisplayRun.totalCandidates} candidates`
                        : ''}
                    </p>
                  ) : null}
                  {reminderCronDisplayRun?.error ? (
                    <p className="text-[10px] text-red-600 leading-snug">
                      {reminderCronDisplayRun.error}
                    </p>
                  ) : null}
                </div>

                <div className="border-t border-gray-100 pt-2 space-y-1">
                  <p className="text-[10px] font-semibold text-gray-700 uppercase tracking-wide">
                    Upcoming Due (CRON)
                  </p>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${automationRunBadgeClass(
                        upcomingCronRunning,
                        upcomingCronDisplayRun
                      )}`}
                    >
                      {automationRunStatusLabel(upcomingCronRunning, upcomingCronDisplayRun)}
                    </span>
                    {!automationJobs.upcomingDueReminder?.enabled ? (
                      <span className="text-[10px] text-amber-700">Disabled</span>
                    ) : null}
                  </div>
                  <p className="text-[10px] text-gray-600">
                    {upcomingCronRunning ? 'Current run' : 'Last run'}:{' '}
                    {formatDateTime(
                      upcomingCronDisplayRun?.startedAt ?? upcomingCronDisplayRun?.finishedAt
                    ) ?? 'No scheduled run yet'}
                  </p>
                  {automationJobs.upcomingDueReminder?.enabled ? (
                    <p className="text-[10px] text-gray-500">
                      Daily at {automationJobs.upcomingDueReminder.runTime} (
                      {automationJobs.upcomingDueReminder.timezone}) ·{' '}
                      {automationJobs.upcomingDueReminder.daysBeforeDue} days window · every{' '}
                      {automationJobs.upcomingDueReminder.frequencyDays} day
                      {automationJobs.upcomingDueReminder.frequencyDays === 1 ? '' : 's'}
                    </p>
                  ) : null}
                  {upcomingCronDisplayRun ? (
                    <p className="text-[10px] text-gray-600">
                      {formatAutomationRunStatsWithLinks(
                        upcomingCronDisplayRun as AutomationRunWithLinks
                      )}
                      {upcomingCronRunning && upcomingCronDisplayRun.totalCandidates > 0
                        ? ` · ${upcomingCronDisplayRun.totalCandidates} candidates`
                        : ''}
                    </p>
                  ) : null}
                </div>

                <div className="border-t border-gray-100 pt-2 space-y-1">
                  <p className="text-[10px] font-semibold text-gray-700 uppercase tracking-wide">
                    Payment Links
                  </p>
                  {paymentLinkJobs ? (
                    <>
                      <div className="flex items-center gap-2">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${paymentLinkBadgeClass(
                            paymentLinkJobs
                          )}`}
                        >
                          {paymentLinkStatusLabel(paymentLinkJobs)}
                        </span>
                        {paymentLinkJobs.isBulkSyncRunning ? (
                          <span className="text-[10px] text-blue-700">Bulk sync running</span>
                        ) : null}
                      </div>
                      <p className="text-[10px] text-gray-500 leading-snug">{paymentLinkJobs.note}</p>
                      <p className="text-[10px] text-gray-600">
                        Via Reminders CRON:{' '}
                        {formatDateTime(
                          paymentLinkCronRun?.finishedAt ?? paymentLinkCronRun?.startedAt
                        ) ?? 'No scheduled run yet'}
                      </p>
                      {paymentLinkCronRun ? (
                        <p className="text-[10px] text-gray-600">
                          {formatPaymentLinkRunStats(paymentLinkCronRun)}
                          {paymentLinkCronRun.status === 'running'
                            ? ` · ${paymentLinkCronRun.totalCandidates} eligible`
                            : ''}
                        </p>
                      ) : null}
                      {paymentLinkJobs.lastBulkRun ? (
                        <>
                          <p className="text-[10px] text-gray-500 pt-1">
                            Manual bulk sync:{' '}
                            {formatDateTime(paymentLinkJobs.lastBulkRun.finishedAt) ?? '—'}
                          </p>
                          <p className="text-[10px] text-gray-500">
                            {paymentLinkJobs.lastBulkRun.created} created ·{' '}
                            {paymentLinkJobs.lastBulkRun.regenerated} updated ·{' '}
                            {paymentLinkJobs.lastBulkRun.errors} errors
                          </p>
                        </>
                      ) : null}
                    </>
                  ) : null}
                </div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              const confirmed = window.confirm(
                'This will sync sites, subscribers, and invoices from CRM. Continue?'
              );
              if (confirmed) {
                syncMutation.mutate();
              }
            }}
            disabled={syncMutation.isPending || syncStatus?.status === 'running'}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {syncMutation.isPending || syncStatus?.status === 'running'
              ? 'Syncing...'
              : 'Sync From CRM'}
          </button>
        </div>
      </div>

      {showLoadError && (
        <div className="bg-white rounded-lg shadow-sm border border-red-100 p-6 text-center">
          <p className="text-sm text-red-600">Failed to load dashboard data.</p>
          <p className="text-sm text-gray-500 mt-1">Please refresh the page to try again.</p>
        </div>
      )}

      {showGlobalEmpty && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-10">
          <EmptyState
            message="No data available yet"
            actionLabel="Create your first Site to begin managing subscribers and reminders."
            actionHref="/sites"
          />
        </div>
      )}

      {/* Row 1 — KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
        <KpiCard
          label="Total Sites"
          value={formatCount(data?.totalSites ?? 0)}
          href="/sites"
          loading={isLoading}
        />
        <KpiCard
          label="Total Subscribers"
          value={formatCount(data?.totalSubscribers ?? 0)}
          href="/subscribers"
          loading={isLoading}
        />
        <KpiCard
          label="Overdue Amount"
          value={formatCurrency(data?.paymentCollection.overdueAmount ?? 0)}
          href="/invoices"
          loading={isLoading}
        />
        <KpiCard
          label="Pending Approvals"
          value={formatCount(data?.pendingApprovals ?? 0)}
          href="/approvals"
          loading={isLoading}
        />
      </div>

      {/* Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <SectionCard title="Payment Collection Status" className="lg:col-span-2">
          {isLoading ? (
            <div className="space-y-4 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 bg-gray-100 rounded" />
              ))}
            </div>
          ) : showGlobalEmpty ? (
            <EmptyState
              message="No data available yet"
              actionLabel="Create your first Site to begin managing subscribers and reminders."
              actionHref="/sites"
            />
          ) : (
            <div>
              <MetricRow
                label="Outstanding Amount"
                value={formatCurrency(data?.paymentCollection.outstandingAmount ?? 0)}
              />
              <MetricRow
                label="Due Today"
                value={formatCurrency(data?.paymentCollection.dueTodayAmount ?? 0)}
                valueClassName="text-orange-600"
              />
              <MetricRow
                label="Overdue Amount"
                value={formatCurrency(data?.paymentCollection.overdueAmount ?? 0)}
                valueClassName="text-red-600"
              />
              <MetricRow
                label="Pending Invoices"
                value={formatCount(data?.paymentCollection.pendingInvoices ?? 0)}
              />
            </div>
          )}
        </SectionCard>

        <SectionCard title="Reminder Activity">
          {isLoading ? (
            <div className="space-y-4 animate-pulse">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-10 bg-gray-100 rounded" />
              ))}
            </div>
          ) : showGlobalEmpty ? (
            <EmptyState
              message="No data available yet"
              actionLabel="Create your first Site to begin managing subscribers and reminders."
              actionHref="/sites"
            />
          ) : (
            <div>
              <MetricRow
                label="Reminders Sent Today"
                value={formatCount(data?.reminderActivity.sentToday ?? 0)}
              />
              <MetricRow
                label="Successful Sends"
                value={formatCount(data?.reminderActivity.successfulSends ?? 0)}
                valueClassName="text-green-700"
              />
              <MetricRow
                label="Failed Sends"
                value={formatCount(data?.reminderActivity.failedSends ?? 0)}
                valueClassName="text-red-600"
              />
              <MetricRow
                label="Last Reminder Time"
                value={
                  formatDateTime(data?.reminderActivity.lastReminderTime) ??
                  'No reminders sent yet'
                }
                valueClassName={
                  data?.reminderActivity.lastReminderTime ? 'text-gray-900' : 'text-gray-500 font-normal text-sm'
                }
              />
            </div>
          )}
        </SectionCard>
      </div>

      {/* Row 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <SectionCard title="Subscribers Requiring Action">
          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="h-14 bg-gray-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : showGlobalEmpty ? (
            <EmptyState
              message="No data available yet"
              actionLabel="Create your first Site to begin managing subscribers and reminders."
              actionHref="/sites"
            />
          ) : (
            <div className="space-y-3">
              <ActionRow
                label="Overdue Subscribers"
                count={data?.actionItems.overdueSubscribers ?? 0}
                href="/invoices"
                countClassName="text-red-600"
              />
              <ActionRow
                label="Due This Week"
                count={data?.actionItems.dueThisWeek ?? 0}
                href="/invoices"
                countClassName="text-orange-600"
              />
              <ActionRow
                label="Pending Approval Payments"
                count={data?.actionItems.pendingApprovalPayments ?? 0}
                href="/approvals"
                countClassName="text-blue-600"
              />
            </div>
          )}
        </SectionCard>

        <SectionCard title="Recent Pending Approvals">
          {isLoading ? (
            <SkeletonCard className="shadow-none border-0 p-0" />
          ) : showGlobalEmpty ? (
            <EmptyState
              message="No data available yet"
              actionLabel="Create your first Site to begin managing subscribers and reminders."
              actionHref="/sites"
            />
          ) : (data?.recentApprovals.length ?? 0) === 0 ? (
            <EmptyState
              message="No pending approvals"
              actionLabel="View All Approvals"
              actionHref="/approvals"
            />
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {data?.recentApprovals.map((item) => {
                  const paidAt =
                    item.paymentOrder.lastStatusCheckedAt ?? item.paymentOrder.updatedAt;
                  return (
                    <a
                      key={item.paymentOrder.id}
                      href="/approvals"
                      className="block py-3.5 hover:bg-gray-50 -mx-2 px-2 rounded-lg transition-colors"
                    >
                      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {item.subscriber.fullname || 'Unknown subscriber'}
                        </span>
                        <div className="flex items-center justify-between sm:justify-end gap-4 sm:gap-6 shrink-0">
                          <span className="text-sm font-semibold text-gray-900">
                            {formatCurrency(item.paymentOrder.totalAmount)}
                          </span>
                          <span className="text-xs text-gray-500 sm:w-36 sm:text-right">
                            {formatDateTime(paidAt) ?? 'No date'}
                          </span>
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
              <div className="mt-5 pt-4 border-t border-gray-100">
                <a
                  href="/approvals"
                  className="text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  View All Approvals
                </a>
              </div>
            </>
          )}
        </SectionCard>
      </div>

      {/* Row 4 — Site Performance */}
      <SectionCard title="Site Performance">
        {isLoading ? (
          <div className="animate-pulse space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-10 bg-gray-100 rounded" />
            ))}
          </div>
        ) : showGlobalEmpty ? (
          <EmptyState
            message="No data available yet"
            actionLabel="Create your first Site to begin managing subscribers and reminders."
            actionHref="/sites"
          />
        ) : (data?.topSites.length ?? 0) === 0 ? (
          <EmptyState
            message="No sites to display"
            actionLabel="Go to Sites"
            actionHref="/sites"
          />
        ) : (
          <div className="overflow-x-auto -mx-6">
            <table className="w-full min-w-[480px]">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Site Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Subscribers
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Outstanding Amount
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {data?.topSites.map((site) => (
                  <tr key={site.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3.5 text-sm font-medium text-gray-900">
                      <a href="/sites" className="hover:text-blue-600">
                        {site.name}
                      </a>
                    </td>
                    <td className="px-6 py-3.5 text-sm text-gray-700 text-right">
                      {formatCount(site.subscriberCount)}
                    </td>
                    <td className="px-6 py-3.5 text-sm font-semibold text-gray-900 text-right">
                      {formatCurrency(site.outstandingAmount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
}
